/**
 * File validation utilities for CV upload
 */

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates file type based on extension and MIME type
 */
export function validateFileType(file: File): FileValidationResult {
  // Check file extension (case-insensitive)
  const fileName = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext)
  );

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: 'Formato de arquivo não suportado. Use PDF, DOC ou DOCX.',
    };
  }

  // Check MIME type
  const hasValidMimeType = ALLOWED_MIME_TYPES.includes(file.type);

  if (!hasValidMimeType && file.type !== '') {
    // Some browsers don't set MIME type for .doc files, so we allow empty type
    // if the extension is valid
    return {
      isValid: false,
      error: 'Tipo de arquivo inválido. Use PDF, DOC ou DOCX.',
    };
  }

  return { isValid: true };
}

/**
 * Validates file size
 */
export function validateFileSize(file: File): FileValidationResult {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    return {
      isValid: false,
      error: `Arquivo muito grande (${fileSizeMB} MB). O tamanho máximo é ${MAX_FILE_SIZE_MB} MB.`,
    };
  }

  if (file.size === 0) {
    return {
      isValid: false,
      error: 'Arquivo vazio. Selecione um arquivo válido.',
    };
  }

  return { isValid: true };
}

/**
 * Validates file completely (type and size)
 */
export function validateFile(file: File): FileValidationResult {
  // Validate type first
  const typeValidation = validateFileType(file);
  if (!typeValidation.isValid) {
    return typeValidation;
  }

  // Then validate size
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }

  return { isValid: true };
}

/**
 * Gets the file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
}

/**
 * Gets the MIME type for API submission
 */
export function getFileMimeType(file: File): string {
  // If browser provides MIME type, use it
  if (file.type) {
    return file.type;
  }

  // Otherwise, infer from extension
  const extension = getFileExtension(file.name);
  switch (extension) {
    case '.pdf':
      return 'application/pdf';
    case '.doc':
      return 'application/msword';
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return 'application/octet-stream';
  }
}
